import { mutationOptions } from "@tanstack/react-query";
import {
  CHANGE_EMAIL_VERSION_2,
  DEL_BUZZ,
  SEARCH_USERS_BY_PREFERENCE,
  UPD_AVA,
  UPD_USER_INF,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { sha1 } from "~/lib/crypto";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import { EVENT_NAMES, trackEvent } from "~/lib/tracking";
import {
  FIREBASE_EVENT_NAME,
  trackFirebaseEvent,
} from "~/lib/tracking/firebase";
import { uploadAvatar } from "~/lib/uploadImage";
import { useAuthStore } from "~/stores/useAuthStore";
import type { User } from "~/types/user";
import { toGender } from "~/types/user";
import type {
  ChangeEmailFormValues,
  ChangeEmailResponse,
  DeletePhotoResponse,
  GetUserInfoResponse,
  SetAvatarResponse,
  UpdateUserInfoRequest,
  UpdateUserInfoResponse,
} from "./types";

// myProfileQueryOptions / updateCallWaitingMutationOptions は lib/queryOptions に移動した
// （複数 feature・route loader・共有フックから参照されるため）。既存 import 互換のため re-export する。
export {
  myProfileQueryOptions,
  updateCallWaitingMutationOptions,
} from "~/lib/queryOptions/myProfile";

const toUser = (data: GetUserInfoResponse): User => {
  return {
    id: data.user_id,
    name: data.user_name,
    email: data.email,
    gender: toGender(data.gender),
    age: data.age,
    avatarUrl: data.ava_id ?? null,
    region: data.region ?? null,
  };
};

export const updateUserInfoMutationOptions = mutationOptions({
  mutationFn: (data: UpdateUserInfoRequest) =>
    apiClient<UpdateUserInfoResponse>(
      UPD_USER_INF,
      data as Record<string, unknown>,
    ),
  onSuccess: (_data, variables) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.myProfile.detail() });
    trackEvent(EVENT_NAMES.EDIT_PROFILE);
    // upd_user_inf はフィールド単位で呼ばれるため、送信フィールドから項目別イベントを判定する
    // （profile_edit の field は about_me / age / avatar の3種のみ。他項目は送らない）
    if (variables.abt?.trim()) {
      trackEvent(EVENT_NAMES.ADD_ABOUT_ME);
      trackFirebaseEvent(FIREBASE_EVENT_NAME.PROFILE_EDIT, {
        field: "about_me",
      });
    }
    if (variables.age !== undefined) {
      trackEvent(EVENT_NAMES.ADD_AGE);
      trackFirebaseEvent(FIREBASE_EVENT_NAME.PROFILE_EDIT, { field: "age" });
    }
  },
});

export const changeEmailMutationOptions = mutationOptions({
  mutationFn: async (data: ChangeEmailFormValues) => {
    const hashedNewPwd = await sha1(data.newPassword);
    return apiClient<ChangeEmailResponse>(CHANGE_EMAIL_VERSION_2, {
      email: data.email,
      original_pwd: data.currentPassword,
      new_pwd: hashedNewPwd,
    });
  },
  onSuccess: (data) => {
    queryClient.setQueryData(queryKeys.myProfile.detail(), data);
    useAuthStore.getState().setUser(toUser(data));
  },
});

// 自分の写真が変わったときに取り直すキャッシュ（プロフィール・写真一覧・公開プロフィール）
const invalidateMyPhotos = () => {
  queryClient.invalidateQueries({ queryKey: queryKeys.myProfile.detail() });
  const userId = useAuthStore.getState().userId;
  if (!userId) return;
  queryClient.invalidateQueries({
    queryKey: queryKeys.user.publicImages(userId),
  });
  queryClient.invalidateQueries({ queryKey: queryKeys.user.profile(userId) });
};

export const uploadAvatarMutationOptions = mutationOptions({
  mutationFn: (blob: Blob) => uploadAvatar(blob),
  onSuccess: () => {
    // アップロード画像は公開画像として蓄積され、アバターも切り替わる
    invalidateMyPhotos();
    trackEvent(EVENT_NAMES.UPLOAD_PROFILE_AVATAR);
    trackFirebaseEvent(FIREBASE_EVENT_NAME.PROFILE_EDIT, { field: "avatar" });
  },
});

// 既存の公開画像をアバターに設定する（前のアバターは公開画像として残る）
export const setAvatarMutationOptions = mutationOptions({
  mutationFn: (imgId: string) =>
    apiClient<SetAvatarResponse>(UPD_AVA, { img_id: imgId }),
  onSuccess: invalidateMyPhotos,
});

// アバターだった画像を消すと BE が次の画像 or デフォルトへ自動で切り替える
export const deletePhotoMutationOptions = mutationOptions({
  mutationFn: (buzzId: string) =>
    apiClient<DeletePhotoResponse>(DEL_BUZZ, { buzz_id: buzzId }),
  onSuccess: invalidateMyPhotos,
});

/**
 * 好みの条件（プロフィール編集の4問）を保存する。
 *
 * ★保存専用のAPIがまだ無く、`search_users_by_preference` が保存と検索を兼ねている。
 *   返ってくる一覧はここでは使わない。分離は改修計画の台帳 #9。
 * ★送った項目だけが更新される（サーバーが軸単位で upsert する）。
 */
export const savePreferencesMutationOptions = mutationOptions({
  mutationFn: (data: Record<string, number>) =>
    apiClient<unknown>(SEARCH_USERS_BY_PREFERENCE, { ...data, take: 1 }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.userPreferences.all });
  },
});
